import { Component } from "react";
import * as Sentry from "@sentry/react";
import { AlertCircle } from "lucide-react";
import { DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";

const STROKE = 1.5;

/**
 * Error boundary for the recording flow.
 *
 * The app has NO other error boundary, so historically any uncaught render
 * error inside a recording stage (notably the online / getDisplayMedia path)
 * tore down the whole React tree → a completely blank page. This boundary
 * contains such a failure and shows a recoverable in-dialog message instead.
 *
 * Scope: recording flow only. It does not wrap the consent gate logic, the
 * mic path, the upload pipeline, or any compliance guard — only the rendered
 * stages, so a render crash degrades to a "try again" card, never a blank.
 */
export default class RecordingErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Report to Sentry (no-op in preview where Sentry isn't initialized). The
    // sentry.js before_send scrubbers strip any PII; recording-flow render
    // errors carry no candidate content anyway.
    try {
      Sentry.captureException(error, {
        tags: { boundary: "recording_flow" },
        extra: { componentStack: info?.componentStack },
      });
    } catch {
      /* never let reporting throw */
    }
    console.error("RecordingErrorBoundary caught an error:", error);
    // Let the parent release the recording lock / timers / live tracks.
    try {
      this.props.onError?.(error);
    } catch {
      /* never let cleanup throw */
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false });
    try {
      this.props.onReset?.();
    } catch {
      /* ignore */
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <>
        <DialogHeader>
          <DialogTitle
            className="font-display text-2xl font-medium"
            data-testid="recording-error-title"
          >
            Couldn’t start screen recording
          </DialogTitle>
          <DialogDescription className="text-[13px] text-ink-500">
            The browser share dialog may have been closed, or screen sharing isn’t available here.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2" data-testid="recording-error-boundary">
          <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={STROKE} />
            <span>
              Something interrupted the recording before it could start. Your earlier
              interviews are unaffected. Please try again — and when the browser asks,
              pick the meeting tab and tick <strong>“Share tab audio”</strong>.
            </span>
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => this.props.onClose?.()}
              className="hp-btn-ghost"
              data-testid="recording-error-close"
            >
              Close
            </button>
            <button
              onClick={this.handleRetry}
              className="hp-btn-primary"
              data-testid="recording-error-retry"
            >
              Try again
            </button>
          </div>
        </div>
      </>
    );
  }
}
